import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ITrade } from '@d-orca/shared/trusstor';
import { Modal } from '@components/ui/Modal/Modal';
import { Button } from '@components/ui/Button/Button';
import { Field } from '@components/ui/Field/Field';
import { GroupedSelect } from '@components/ui/GroupedSelect/GroupedSelect';
import { TradePill } from '@components/ui/TradePill/TradePill';
import type { IGroupedOptions } from '@components/Pages/AnalyticsPage/analytics.types';
import {
  DuplicateProfessionError,
  ProfessionInUseError,
  type IAdminProfession,
} from '@apis/adminProfessions';
import {
  useCreateProfession,
  useDeleteProfession,
  useUpdateProfessionColor,
  useAdminTrades,
} from '../hooks/useAdminProfessions';
import { humanizeCamelCase, tradeLabel } from '@utils/tradeLabel';
import { TrashIcon } from '@/utils/icons';

import { ColorSwatchPicker } from './ColorSwatchPicker/ColorSwatchPicker';
import styles from './ProfessionDialog.module.css';

interface IProfessionDialogProps {
  siteId: string;
  open: boolean;
  /** null → create mode; set → edit mode. */
  profession: IAdminProfession | null;
  onClose: () => void;
}

/**
 * Create/edit dialog for a profession.
 *
 * Both modes share one form. Only the colour is editable once a profession
 * exists — the API supports no other update — so in edit mode the identity
 * fields are shown disabled rather than hidden, which keeps the two modes
 * recognisably the same dialog.
 */
export function ProfessionDialog({ siteId, open, profession, onClose }: IProfessionDialogProps) {
  const { t } = useTranslation();
  const isEdit = profession !== null;

  const [tradeId, setTradeId] = useState<string | null>(profession?.tradeId ?? null);
  const [specialty, setSpecialty] = useState(profession?.specialty ?? '');
  const [contractor, setContractor] = useState(profession?.contractor ?? '');
  const [pickedColor, setPickedColor] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  // Editing can't change the trade, so the catalog is only needed for create.
  const { data: tradesData } = useAdminTrades(siteId, open && !isEdit);
  const trades = useMemo(() => tradesData?.trades ?? [], [tradesData]);

  const tradeGroups = useMemo<IGroupedOptions[]>(() => {
    // In edit mode the select is disabled and only has to render the current
    // trade, so it gets a single-entry group instead of the whole catalog.
    if (isEdit) {
      return [
        {
          groupId: 'current',
          groupLabel: '',
          options: [{ id: profession.tradeId, label: tradeLabel(t, profession.tradeId) }],
        },
      ];
    }
    const byGroup = new Map<string, ITrade[]>();
    for (const trade of trades) {
      const group = trade.tradeGroup || 'general';
      const list = byGroup.get(group);
      if (list) list.push(trade);
      else byGroup.set(group, [trade]);
    }
    return Array.from(byGroup.entries())
      .map(([group, groupTrades]) => ({
        groupId: group,
        groupLabel: t(`trades.${group}`, { defaultValue: humanizeCamelCase(group) }),
        options: [...groupTrades]
          .sort((a, b) => tradeLabel(t, a.tradeId).localeCompare(tradeLabel(t, b.tradeId)))
          .map((trade) => ({ id: trade.tradeId, label: tradeLabel(t, trade.tradeId) })),
      }))
      .sort((a, b) => a.groupLabel.localeCompare(b.groupLabel));
  }, [isEdit, profession, trades, t]);

  // On create the trade's own colour is the default; on edit the stored colour
  // already arrives project-resolved from the backend.
  const selectedTrade = trades.find((trade) => trade.tradeId === tradeId) ?? null;
  const initialColor = profession?.backgroundColor ?? selectedTrade?.backgroundColor ?? null;
  const color = pickedColor ?? initialColor;

  function handleClose() {
    setTradeId(profession?.tradeId ?? null);
    setSpecialty(profession?.specialty ?? '');
    setContractor(profession?.contractor ?? '');
    setPickedColor(null);
    setErrorKey(null);
    createMutation.reset();
    updateMutation.reset();
    deleteMutation.reset();
    onClose();
  }

  // The hooks own the API calls + cache invalidation; the dialog keeps its
  // own feedback (inline errorKey) and close-on-success via mutate options —
  // TanStack runs the hook's onSuccess (invalidation) before the call-site's.
  const createMutation = useCreateProfession(siteId);
  const updateMutation = useUpdateProfessionColor(siteId);
  const deleteMutation = useDeleteProfession(siteId);

  // `busy` gates every control (no concurrent writes), but the submit label
  // must track only its own mutation — otherwise deleting makes the Save
  // button read "Saving…" alongside the Delete button's "Deleting…".
  const saving = isEdit ? updateMutation.isPending : createMutation.isPending;
  const busy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;
  const canSubmit = isEdit ? color !== null && color !== initialColor : tradeId !== null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t(isEdit ? 'adminProfessions.edit.title' : 'adminProfessions.create.title')}
      footer={
        <>
          {isEdit && (
            <Button
              variant="danger"
              size="md"
              disabled={busy}
              onClick={() => {
                setErrorKey(null);
                // No confirm step — deletion is guarded server-side and
                // refused with a specific reason.
                deleteMutation.mutate(profession._id, {
                  onSuccess: handleClose,
                  onError: (err) => {
                    setErrorKey(
                      err instanceof ProfessionInUseError
                        ? err.reason === 'tags'
                          ? 'adminProfessions.deleteBlockedTags'
                          : 'adminProfessions.deleteBlockedActivities'
                        : 'adminProfessions.deleteError',
                    );
                  },
                });
              }}
            >
              <TrashIcon size={14} />
              {deleteMutation.isPending
                ? t('adminProfessions.delete.submitting')
                : t('adminProfessions.delete.submit')}
            </Button>
          )}
          <span className={styles.footerSpacer} />
          <Button variant="secondary" size="md" onClick={handleClose}>
            {t('adminProfessions.cancel')}
          </Button>
          <Button
            variant="primary"
            size="md"
            disabled={!canSubmit || busy}
            onClick={() => {
              setErrorKey(null);
              if (isEdit) {
                updateMutation.mutate(
                  { professionId: profession._id, backgroundColor: color! },
                  {
                    onSuccess: handleClose,
                    onError: () => setErrorKey('adminProfessions.updateError'),
                  },
                );
              } else {
                createMutation.mutate(
                  {
                    tradeId: tradeId!,
                    ...(specialty.trim() && { specialty: specialty.trim() }),
                    ...(contractor.trim() && { contractor: contractor.trim() }),
                    // Only an explicit swatch pick is sent — when omitted the
                    // backend falls back to the trade's default color (which
                    // may not be a strict #rrggbb value and would fail body
                    // validation if echoed back).
                    ...(pickedColor && { backgroundColor: pickedColor }),
                  },
                  {
                    onSuccess: handleClose,
                    onError: (err) => {
                      setErrorKey(
                        err instanceof DuplicateProfessionError
                          ? 'adminProfessions.duplicateError'
                          : 'adminProfessions.createError',
                      );
                    },
                  },
                );
              }
            }}
          >
            {saving
              ? t(isEdit ? 'adminProfessions.edit.submitting' : 'adminProfessions.create.submitting')
              : t(isEdit ? 'adminProfessions.edit.submit' : 'adminProfessions.create.submit')}
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        {tradeId && (
          <div className={styles.preview}>
            <TradePill
              label={tradeLabel(t, tradeId)}
              backgroundColor={color ?? undefined}
              tradeId={tradeId}
            />
          </div>
        )}

        <div className={styles.formField}>
          <span className={styles.formLabel}>{t('adminProfessions.field.trade')}</span>
          <GroupedSelect
            value={tradeId}
            onChange={(next) => {
              setTradeId(next);
              setErrorKey(null);
            }}
            groups={tradeGroups}
            placeholder={t('adminProfessions.field.tradePlaceholder')}
            ariaLabel={t('adminProfessions.field.trade')}
            disabled={isEdit}
            elevated
          />
        </div>

        <Field
          label={t('adminProfessions.field.specialty')}
          value={specialty}
          onChange={(e) => setSpecialty(e.target.value)}
          placeholder={isEdit ? undefined : t('adminProfessions.field.optional')}
          disabled={isEdit}
          maxLength={200}
        />
        <Field
          label={t('adminProfessions.field.contractor')}
          value={contractor}
          onChange={(e) => setContractor(e.target.value)}
          placeholder={isEdit ? undefined : t('adminProfessions.field.optional')}
          disabled={isEdit}
          maxLength={200}
        />

        <div className={styles.formField}>
          <span className={styles.formLabel}>{t('adminProfessions.field.color')}</span>
          <ColorSwatchPicker value={color} onChange={setPickedColor} />
        </div>

        {errorKey && <div className={styles.formError}>{t(errorKey)}</div>}
      </div>
    </Modal>
  );
}
